import { useCallback, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'

/** How long a write that hasn't shown up in the location yet is still trusted. A safety net, not the mechanism. */
const PENDING_SEARCH_TTL_MS = 1000

/**
 * The last write, until the URL has caught up with it.
 *
 * Applying a search writes its criteria and its filters to the URL back to back — from separate hooks
 * that each captured `location.search` at render, and not even in the same tick (the form submits
 * asynchronously). Without this, whichever writes second starts from the stale string and silently
 * drops the first one's params. A write stays pending while the location is still the one it was
 * based on, and is forgotten as soon as a render sees the location move on.
 */
let pendingWrite: { base: string; search: string; at: number } | undefined

const currentSearchFor = (locationSearch: string): string =>
    pendingWrite && pendingWrite.base === locationSearch && Date.now() - pendingWrite.at < PENDING_SEARCH_TTL_MS
        ? pendingWrite.search
        : locationSearch

/**
 * Returns a function that rewrites the URL's query string through `transform` (which takes the
 * current string and returns the new one, "?"-prefixed or empty), keeping the path and hash and
 * replacing history rather than pushing a new entry.
 *
 * react-router's `setSearchParams` drops the current hash, which matters for pages that use it to
 * pick a tab, so this navigates with an explicit `to` object instead.
 */
export const useUrlSearchWriter = () => {
    const location = useLocation()
    const navigate = useNavigate()

    // A pending write belongs to the page that made it: leaving the page (or ending a test) forgets it,
    // so a later page that happens to start from the same URL doesn't build on it.
    useEffect(
        () => () => {
            pendingWrite = undefined
        },
        []
    )

    return useCallback(
        (transform: (currentSearch: string) => string) => {
            const search = transform(currentSearchFor(location.search))
            pendingWrite = { base: location.search, search, at: Date.now() }
            navigate(
                { pathname: location.pathname, search, hash: location.hash },
                { replace: true, preventScrollReset: true }
            )
        },
        [location.hash, location.pathname, location.search, navigate]
    )
}

export default useUrlSearchWriter
